import { useMemo } from 'react';

import aa from 'search-insights';

import qualifyingItemBadge1 from '@/assets/images/qualifying-item-badge-1.svg';
import qualifyingItemBadge2 from '@/assets/images/qualifying-item-badge-2.svg';
import qualifyingItemBadge3 from '@/assets/images/qualifying-item-badge-3.svg';
import { ALGOLIA_INDEX_NAME } from '@/config/env';
import {
  IbmPlexItalic,
  IbmPlexItalicBold,
  IbmPlexItalicMedium,
  IbmPlexItalicSemiBold,
  SweetSansPro,
  SweetSansProBold,
  SweetSansProExtraLight,
  SweetSansProLight,
  SweetSansProMedium,
  SweetSansProSemiBold,
} from '@/config/fonts';
import useEzShip from '@/helpers/hooks/useEzShip';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { useAppContext } from '@/layouts/AppProvider';
import { useAppSelector } from '@/store';
import { AlgoliaProduct } from '@/types/Algolia';

import { BaseProductCard } from './BaseProductCard';

type AlgoliaProductCardProps = {
  className?: string;
  index: number;
  product: AlgoliaProduct;
  queryID: string;
  event?: 'clickedObjectIDsAfterSearch' | 'clickedObjectIDs';
};

const AlgoliaProductCard = ({
  className,
  product,
  index,
  queryID,
  event = 'clickedObjectIDsAfterSearch', // eslint-disable-next-line sonarjs/cognitive-complexity
}: AlgoliaProductCardProps) => {
  const { webalias, generateURL: generateWebaliasURL } = useWebAlias();
  const { ezShipId, generateURL: generateEzShipURL } = useEzShip();
  const { isBackOffice } = useAppContext();
  const cookieConsent = useAppSelector(state => state.slices.appConfig.isCookieConsentRejected);

  const freeShippingImage = useMemo(() => {
    let image = null;

    if (product.TruckIconFreeShippingQualified?.includes('1')) {
      image = qualifyingItemBadge1;
    }

    if (product.TruckIconFreeShippingQualified?.includes('2')) {
      image = qualifyingItemBadge2;
    }

    if (product.TruckIconFreeShippingQualified?.includes('3')) {
      image = qualifyingItemBadge3;
    }

    return image;
  }, [product]);

  const name = product.ParentGMItem?.ItemName || product.GMItem?.ItemName || product.ItemName;
  const price = product.ParentGMItem?.Price || product.GMItem?.Price || product.Price;
  const thumbnail = product.ParentGMItem?.MainImage || product.GMItem?.MainImage || product.MainImage;
  const slug = product.ParentGMItem?.Slug || product.GMItem?.Slug || product.Slug;
  const itemCode =
    product.ParentGMItem?.ItemCode ||
    product.GMItem?.ItemCode ||
    product.ParentGMCode ||
    product.GMCode ||
    product.ItemCode;
  const strikeoutPrice =
    product.ParentGMItem?.StrikeOutPrice || product.GMItem?.StrikeOutPrice || product.StrikeOutPrice;
  const hasStrikeoutPrice =
    product.ParentGMItem?.HasStrikeOutPrice || product.GMItem?.HasStrikeOutPrice || product.HasStrikeOutPrice;

  const addQueryParamsInUrl =
    (event === 'clickedObjectIDsAfterSearch' && cookieConsent === true) ||
    product.ParentGMItem?.ItemCode ||
    product.GMItem?.ItemCode;

  const urlQueryParams = new URLSearchParams({
    ...(product.ParentGMItem?.ItemCode ? { itemCode: product.GMItem?.ItemCode } : {}),
    ...(product.ParentGMItem?.ItemCode && product.GMItem?.ItemCode ? { innerItemCode: product?.ItemCode } : {}),
    ...(product.GMItem?.ItemCode && !product.ParentGMItem?.ItemCode ? { itemCode: product.ItemCode } : {}),
    ...(event === 'clickedObjectIDsAfterSearch' && cookieConsent === true ? { queryID: queryID } : {}),
  });

  const url = generateWebaliasURL(
    generateEzShipURL(`/product/${encodeURIComponent(slug || itemCode)}`, ezShipId) +
      (addQueryParamsInUrl ? '?' : '') +
      urlQueryParams.toString(),
    isBackOffice ? 'shopping' : webalias
  );

  const isOutOfStock = product?.ParentGMItem
    ? product?.ParentGMItem?.IsOutOfStock
    : product?.GMItem
    ? product?.GMItem?.IsOutOfStock
    : product?.IsOutOfStock;

  const pawClubPromotionIcon = product?.ParentGMItem
    ? product?.ParentGMItem?.IconForPawClubPromotion
    : product?.GMItem
    ? product?.GMItem?.IconForPawClubPromotion
    : product?.IconForPawClubPromotion;

  const isCartOperationsDisabled = product?.ParentGMItem
    ? product?.ParentGMItem?.DisableCartOperations
    : product?.GMItem
    ? product?.GMItem?.DisableCartOperations
    : product?.DisableCartOperations;

  const isPromotionPDFVisible = product?.ParentGMItem
    ? product?.ParentGMItem?.IsPromotionPDFVisible
    : product?.GMItem
    ? product?.GMItem?.IsPromotionPDFVisible
    : product?.IsPromotionPDFVisible;

  const promotionPDF =
    (product?.ParentGMItem
      ? product?.ParentGMItem?.PromotionPDF
      : product?.GMItem
      ? product?.GMItem?.PromotionPDF
      : product?.PromotionPDF) ?? '';

  const isPromotionFeatureVisible = !!(product?.ParentGMItem
    ? product?.ParentGMItem?.IsPromotionFeatureVisible
    : product?.GMItem
    ? product?.GMItem?.IsPromotionFeatureVisible
    : product?.IsPromotionFeatureVisible);

  const promotionFeature =
    (product?.ParentGMItem
      ? product?.ParentGMItem?.PromotionFeature
      : product?.GMItem
      ? product?.GMItem?.PromotionFeature
      : product?.PromotionFeature) ?? '';

  const trackAlgoliaEvents = () => {
    if (cookieConsent !== true) return;
    aa('getUserToken', {}, (err, userToken) => {
      if (err) {
        console.error(err);
        return;
      }

      aa('getAuthenticatedUserToken', {}, (err, authenticatedUserToken) => {
        if (err) {
          console.error(err);
          return;
        }
        aa(event, {
          userToken: `${userToken || ''}`,
          authenticatedUserToken: `${authenticatedUserToken || ''}`,
          index: ALGOLIA_INDEX_NAME,
          eventName: 'Product Clicked',
          queryID: queryID,
          objectIDs: [itemCode],
          positions: [index],
        });
      });
    });
  };

  return (
    <div
      className={cn(
        SweetSansProExtraLight.variable,
        SweetSansProLight.variable,
        SweetSansPro.variable,
        SweetSansProMedium.variable,
        SweetSansProSemiBold.variable,
        SweetSansProBold.variable,
        IbmPlexItalic.variable,
        IbmPlexItalicBold.variable,
        IbmPlexItalicMedium.variable,
        IbmPlexItalicSemiBold.variable,
        'flex justify-center',
        className
      )}
    >
      <BaseProductCard
        href={url}
        className={className}
        price={price}
        name={name}
        thumbnail={thumbnail}
        isOutOfStock={isOutOfStock}
        onClick={trackAlgoliaEvents}
        pawClubPromotionIcon={pawClubPromotionIcon}
        isCartOperationsDisabled={isCartOperationsDisabled}
        isPromotionPDFVisible={isPromotionPDFVisible}
        promotionPDF={promotionPDF}
        isPromotionFeatureVisible={isPromotionFeatureVisible}
        promotionFeature={promotionFeature}
        hasStrikeOutPrice={hasStrikeoutPrice}
        strikeOutPrice={strikeoutPrice}
        showQualifyingBadge={true}
        productTitleClassName="v2-font-sweet-sans-pro-light"
        productImgContainerClassName="v2-bg-[#F3F6F5]"
        freeShippingImage={freeShippingImage}
      />
    </div>
  );
};

export default AlgoliaProductCard;
